// No `leaflet` import: D10 moved every Leaflet call this file used to make into
// `shared/leaflet/cluster.layer.ts`, and the import outlived them.
import { ClusterLayer } from '../leaflet/cluster.layer';
import { AisMapContext, DisplayPort } from '../contracts/ais-map.context';

/**
 * The state this module must be TOLD rather than own (rule R5 / phase-4
 * boundary): both are track-mode facts that live with the track feature —
 * on the facade until phase 6 gives that feature its own home.
 */
export interface DisplayOptions {
  /**
   * MMSI the map is focused on while a track plays — every OTHER live vessel
   * is dimmed to near-invisible. Null when nothing is dimmed.
   */
  focusMmsi: () => string | null;
  /**
   * MMSI of the vessel the loaded track belongs to (null = no track drawn).
   * Its live marker is held at a fixed fade so the moving ghost reads as
   * "the" vessel — independent of the fleet dim.
   */
  tracedMmsi: () => string | null;
}

/**
 * THE DISPLAY PASS — ship-type filter, count-bubble clustering and name-label
 * thinning, in strictly that order (invariant I3: each stage decides what the
 * next one sees). Extracted verbatim from `VoyageMapController` (phase 4,
 * REFACTOR-PLAN.md §7); peers are reached through `ctx` ports only (rule R2).
 *
 * One pass, never independent passes: the last pass to run wins, and which
 * runs last depends on the order a refresh, a filter change and a track load
 * happen to arrive in. The grid maths and bubble markup are the pure model's
 * (`cluster.model.ts`); what lives here is everything Leaflet — projection,
 * the marker pool, the layers.
 */
export class DisplayController implements DisplayPort {
  // ── Clustering ──────────────────────────────────────────────────────────────
  /**
   * The count-bubble pass, which is no longer this file's (D10). It moved to
   * `shared/leaflet/cluster.layer.ts` unchanged when AISstream needed grouping
   * too: the grid maths and the Leaflet plumbing were never Voyage Radar's,
   * only the three decisions below were.
   *
   * The map is reached through a thunk because `ctx.host.map` is replaced on
   * every re-attach; the hooks read `ctx` lazily for the same reason. No `hide`
   * hook - the layer's default (opacity + `display`, together) is exactly what
   * `setMarkerHidden` did here.
   */
  private readonly _cluster = new ClusterLayer(() => this.ctx.host.map, {
    // The filter pass's verdict. A hull it hid is not ours to absorb or restore.
    visible: (marker: any) => marker._vrVisible !== false,
    // Never absorbs the selected vessel: it is the one hull the operator is
    // tracking, and a selection that vanishes into a count is a selection lost.
    absorbable: (marker: any) => {
      const activeMmsi = this.ctx.selection.activeMmsi();
      if (!activeMmsi) return true;
      const props = marker?.feature?.properties ?? {};
      return String(props['MMSI'] ?? '') !== activeMmsi;
    },
    // Zoom cannot separate them - the card names the hulls instead.
    onIndivisible: (at: any, members: any[]) =>
      this.ctx.popup.openClusterPopupAt(
        at,
        members.map((m) => {
          const p = m?.feature?.properties ?? {};
          return { name: p['Name'], mmsi: p['MMSI'] };
        }),
      ),
  });

  // ── Name labels ─────────────────────────────────────────────────────────────
  /**
   * Screen footprint one label reserves, in px. Labels are thinned to one per
   * cell — see `applyVesselLabels`. Wider than a short name on purpose: the cell
   * has to cover the label AND the gap to the next one, or the survivors still
   * collide.
   */
  private static readonly LABEL_CELL_X = 112;
  private static readonly LABEL_CELL_Y = 26;
  /** Viewport listener that re-runs the display pass (null while detached). */
  private _viewportRepaint: (() => void) | null = null;
  /** True while any label is bound — lets an off-state pass skip the walk. */
  private _labelsPainted = false;

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: DisplayOptions,
  ) {}

  // ── Ship-type filter ────────────────────────────────────────────────────────
  /**
   * Groups features by ship type (colour + count taken from the data) and pushes
   * the result to the playback controller, so the filter chips reflect what is
   * actually loaded.
   */
  computeAvailableTypes(features: any[]): void {
    const groups = new Map<string, { color: string; count: number }>();
    for (const f of features || []) {
      const p = f?.properties || f || {};
      const label = p.shipType || p['Ship Type'] || 'Unknown';
      const color = p.color || p['Color'] || 'ORANGE';
      const g = groups.get(label);
      if (g) g.count += 1;
      else groups.set(label, { color, count: 1 });
    }
    const types = Array.from(groups.entries())
      .map(([label, g]) => ({ code: label, label, color: g.color, count: g.count }))
      .sort((a, b) => b.count - a.count);
    this.ctx.host.playback.setAvailableTypes(types);
  }

  /** Filters a raw feature list by the active ship types (empty set = show all). */
  applyTypeFilter(features: any[]): any[] {
    const filter = this.ctx.host.playback.snapshot().typeFilter;
    if (!filter || filter.size === 0) return features || [];
    return (features || []).filter((f) => {
      const p = f?.properties || f || {};
      const label = p.shipType || p['Ship Type'] || 'Unknown';
      return filter.has(label);
    });
  }

  /**
   * Re-applies the ship-type filter to the LIVE (non-playback) view by toggling
   * marker visibility on the existing layer — no second layer, so Spire and
   * ClydeSpace rendering is never touched.
   *
   * The ONE place a live marker's opacity is decided. Three rules stack here
   * rather than in three passes, because passes fight: the last one to run wins,
   * and which runs last depends on the order a refresh, a filter change and a
   * track load happen to arrive in.
   *
   * What is DRAWN is then decided downstream, in `applyVesselDisplay` — a strict
   * pipeline (visible → clustered → labelled), never a second independent pass:
   * each stage only refines what this one settled, and all three re-run together.
   */
  applyTypeFilterToLive(): void {
    if (this.ctx.host.playback.mode === 'playback') return;
    if (!this.ctx.snapshot.dataActive || !this.ctx.snapshot.liveLayer) return;
    const filter = this.ctx.host.playback.snapshot().typeFilter;
    const showAll = !filter || filter.size === 0;
    const focus = this.opts.focusMmsi();
    const traced = this.opts.tracedMmsi();
    this.ctx.snapshot.liveLayer.eachLayer((layer: any) => {
      const props = layer?.feature?.properties ?? {};
      const label = props['Ship Type'] || 'Unknown';
      const passesFilter = showAll || filter.has(label);
      const mmsi = String(props['MMSI'] ?? '');
      // Filter first: a vessel the user has filtered out stays hidden even when
      // it is the one being traced.
      // Then the traced hull, held down so the moving ghost — not this static
      // marker — reads as the vessel.
      // Then the fleet dim, which only applies while a focus MMSI is set:
      // dimmed rather than hidden, because the surrounding traffic is still
      // context for the track, it just must not compete with it.
      const opacity = !passesFilter
        ? 0
        : traced && mmsi === traced
        ? 0.35
        : focus && mmsi !== focus
        ? 0.12
        : 1;
      if (typeof layer.setOpacity === 'function') {
        layer.setOpacity(opacity);
      }
      const el = typeof layer.getElement === 'function' ? layer.getElement() : null;
      if (el) {
        (el as HTMLElement).style.display = passesFilter ? '' : 'none';
      }
      // Clustering and labels both ride this decision: only a hull drawn at full
      // strength is a candidate for either. A cluster must not count a vessel
      // the user filtered out, and a tooltip is its own element in Leaflet's
      // tooltip pane — it inherits neither the marker's opacity nor its
      // `display:none`, so a filtered-out vessel would otherwise keep a name
      // floating over empty water.
      //
      // It also means clustering and the fleet dim never stack: while a track
      // holds the fleet at 0.12 there are no candidates, so no bubbles form. The
      // two are alternative answers to the same clutter, and running both would
      // hide the traffic around the track that the dim exists to keep visible.
      //
      // Recorded on the marker rather than acted on here, because what a
      // candidate actually gets also depends on the OTHER candidates — that is
      // `applyVesselDisplay`, below, over the whole layer at once.
      layer._vrVisible = opacity === 1;
    });
    this.applyVesselDisplay(this.ctx.snapshot.liveLayer);
  }

  /**
   * Turns the visibility decision above into what is actually on screen, in
   * order: cluster the crowded cells, then label whatever survived.
   *
   * Order matters and is the whole reason these are not two independent passes —
   * a hull absorbed into a bubble must not also be carrying a name, and the label
   * thinning must not spend one of its cells on a marker that is now hidden.
   */
  applyVesselDisplay(layer: any): void {
    if (layer && typeof layer.eachLayer === 'function') this.syncViewportRepaint();
    this.applyVesselClusters(layer);
    this.applyVesselLabels(layer);
    // Last, because it reads what the two passes above settled: the ring belongs
    // on a hull that is on screen, and on nothing otherwise.
    this.ctx.selection.syncActiveHalo(layer);
    // Same reason, for the saved-vessel markers: whether one collapses to a ring
    // around a snapshot hull depends on whether that hull ended up painted.
    this.ctx.located.syncLocatedTwins();
  }

  /**
   * Replaces crowded groups of vessels with one count bubble each.
   *
   * A thousand hulls in one AOI is not a chart at any zoom that shows the whole
   * area — the arrows overlap into texture, and the only way to read it was to
   * zoom until they separated. A bubble says how many are down there and splits
   * into its members as the zoom makes room for them.
   *
   * The grid is anchored to WORLD pixels (`map.project` at the current zoom), not
   * to the viewport. That distinction is the difference between clustering that
   * feels solid and clustering that shimmers: bucket by screen position and the
   * grid slides across the vessels as you pan, so counts flicker and bubbles jump
   * between cells under a drag that changed nothing. In world pixels the cells
   * are pinned to the sea floor — a pan cannot re-bucket anything, and only a
   * zoom (which genuinely changes what fits) redraws them.
   *
   * Never absorbs the selected vessel: it is the one hull the operator is
   * tracking, and a selection that vanishes into a count is a selection lost.
   */
  private applyVesselClusters(layer: any): void {
    if (!layer || typeof layer.eachLayer !== 'function') {
      this.clearVesselClusters();
      return;
    }
    // Collected rather than walked twice: `ClusterLayer` makes three passes over
    // the markers (bucket, then restore, then hide) and takes an array so it
    // never has to learn what a Leaflet LayerGroup is.
    const markers: any[] = [];
    layer.eachLayer((marker: any) => markers.push(marker));
    this._cluster.apply(this.clustersOn, markers);
  }

  /** Takes every bubble off the map - the vessels they stood for have gone. */
  clearVesselClusters(): void {
    this._cluster.clear();
  }

  /**
   * Decides which of a layer's markers actually carry a name label, and binds or
   * removes to match. Two rules, both of them there because "a label on every
   * vessel" does not survive a real AOI:
   *
   *   in view — a label on a vessel that is off screen shows nothing and costs
   *     the same. Padded by a fifth, so a hull just past the edge can still push
   *     its name in, and small pans do not thrash the bindings.
   *
   *   decluttered — one label per `LABEL_CELL` of screen, first marker in layer
   *     order wins. A thousand hulls in one AOI is a thousand tooltip elements
   *     that Leaflet re-positions on every zoom (which is the delay), and a
   *     thousand names over each other is unreadable anyway. Thinning fixes both
   *     at once: what is left is the labels you could actually have read.
   *
   * Zoom in and the cells cover less water, so more names appear — the density
   * of labels stays roughly constant while the density of vessels does not.
   */
  private applyVesselLabels(layer: any): void {
    const map = this.ctx.host.map;
    if (!map || !layer || typeof layer.eachLayer !== 'function') return;
    const show = this.labelsOn;
    // Nothing bound and nothing wanted: no reason to walk the layer at all. This
    // is the common case — it keeps the per-frame call in `renderStep` free.
    if (!show && !this._labelsPainted) return;
    const zoom = map.getZoom();
    const bounds = map.getBounds().pad(0.2);
    const taken = new Set<string>();
    layer.eachLayer((marker: any) => {
      const props = marker?.feature?.properties ?? {};
      // A hull inside a bubble is not on screen to be named, and spending one of
      // the thinning cells on it would cost a name that IS visible.
      let wanted = show && marker._vrVisible !== false && marker._vrClustered !== true;
      if (wanted) {
        const latlng = typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
        wanted = !!latlng && bounds.contains(latlng);
        if (wanted) {
          // World pixels, not container pixels — same reasoning as the cluster
          // grid: anchored to the viewport, a pan slides the grid across the
          // fleet and names hop from hull to hull under a drag.
          const pt = map.project(latlng, zoom);
          const cell = `${Math.floor(pt.x / DisplayController.LABEL_CELL_X)}:${Math.floor(
            pt.y / DisplayController.LABEL_CELL_Y
          )}`;
          if (taken.has(cell)) wanted = false;
          else taken.add(cell);
        }
      }
      this.setVesselLabel(marker, props, wanted);
    });
    this._labelsPainted = taken.size > 0;
  }

  /** Whichever layer the clusters and labels belong to right now. */
  get displayLayer(): any {
    return this.ctx.host.playback.mode === 'playback'
      ? this.ctx.snapshot.playbackLayer
      : this.ctx.snapshot.liveLayer;
  }

  /**
   * Clusters and labels are both viewport-scoped, so the view moving has to
   * recompute them.
   *
   * `moveend`/`zoomend` rather than `move`/`zoom`: mid-gesture rebuilding is
   * invisible under the animation and would put a walk of the whole layer inside
   * every animation frame. Attached from the first display pass and dropped when
   * the vessels leave the map, so an idle map with nothing on it pays nothing.
   */
  private syncViewportRepaint(): void {
    const map = this.ctx.host.map;
    if (!map || this._viewportRepaint) return;
    this._viewportRepaint = () => this.applyVesselDisplay(this.displayLayer);
    map.on('moveend zoomend', this._viewportRepaint);
  }

  /** Stops recomputing on view changes (nothing left to recompute). */
  stopViewportRepaint(): void {
    if (!this._viewportRepaint) return;
    this.ctx.host.map?.off('moveend zoomend', this._viewportRepaint);
    this._viewportRepaint = null;
  }

  /** True while the T control is on. The flag lives on the playback controller. */
  get labelsOn(): boolean {
    return this.ctx.host.playback.snapshot().showLabels === true;
  }

  /**
   * True while vessels are being grouped into bubbles.
   *
   * `!== false`, not `=== true`: the field is optional on `PlaybackState` (see
   * there), and grouping is the default — an absent flag must not read as an
   * operator who turned it off.
   */
  private get clustersOn(): boolean {
    return this.ctx.host.playback.snapshot().clusterVessels !== false;
  }

  /**
   * Binds (or removes) one vessel marker's permanent name label — the T control,
   * for both the live snapshot and the playback frames.
   *
   * The offset is measured off the marker's OWN icon rather than fixed: the two
   * paths that draw live vessels use different glyph sizes (50px from the sensor
   * pull, 24px from a recalled refresh), and one constant would either sit on top
   * of the bigger one or float away from the smaller one.
   */
  private setVesselLabel(marker: any, props: any, show: boolean): void {
    if (!marker || typeof marker.bindTooltip !== 'function') return;
    const hasTooltip = typeof marker.getTooltip === 'function' && !!marker.getTooltip();
    // `||`, not `??`: a vessel that broadcasts no name arrives as '', which is
    // not nullish — and a PERMANENT tooltip bound to '' is an empty box parked
    // over the water for as long as labels are on. Same reasoning as
    // `toggleGhostLabel`.
    const text = show
      ? String(props?.['Name'] ?? '') || String(props?.['MMSI'] ?? '')
      : '';
    if (!text) {
      if (hasTooltip) marker.unbindTooltip();
      return;
    }
    if (hasTooltip) {
      marker.setTooltipContent(text);
      return;
    }
    const iconWidth = marker.options?.icon?.options?.iconSize?.[0] ?? 24;
    marker.bindTooltip(text, {
      permanent: true,
      direction: 'right',
      offset: [Math.round(iconWidth / 2 + 4), 0],
      className: 'ais-vessel-label',
    });
  }

  /**
   * Nulls the cluster layer and the labels-painted flag WITHOUT touching the
   * map — for the task-switch reset, which runs inside a shared sweep that has
   * already taken every layer off (invariant I1: a removeLayer here would
   * operate on a map that no longer holds them). The next display pass builds
   * a fresh bubble layer of its own.
   */
  dropRefs(): void {
    this._cluster.dropRefs();
    this._labelsPainted = false;
  }
}
